import { useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  Title,
  SimpleList,
  useCreatePath,
} from "react-admin";
import type { RaRecord } from "react-admin";
import { Box, Button, Tab, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { fromGregorian } from "@/utils/hijri-date-utils";
import { formatListDate, formatWeekdayFullUtc, parseDayjs } from "@/utils/date-format";
import { razaEventTitle } from "@/utils/raza-event-display";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { formatRazaEventSadaratListLabel } from "@/utils/format-raza-event-sadarat-display";

const formatMajlisDateUtc = (date: unknown) =>
  date ? formatListDate(String(date), { utc: true }) : "—";

const formatMajlisDayOfWeekUtc = (date: unknown) =>
  date ? formatWeekdayFullUtc(String(date)) : "—";

const formatMajlisHijriUtc = (date: unknown) =>
  date ? fromGregorian(parseDayjs(String(date), true).toDate(), "code") : "—";

function pastAttendanceCount(record: RaRecord): number {
  const raw = record.attendanceCount;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "" && Number.isFinite(Number(raw)))
    return Number(raw);
  return 0;
}

export default function RazaEventsUpcomingList() {
  const [attendanceTab, setAttendanceTab] = useState<"upcoming" | "past">("upcoming");
  const theme = useTheme();
  const isNarrow = useMediaQuery((t) => t.breakpoints.down("md"), { noSsr: true });
  const createPath = useCreatePath();
  const attendanceBase = createPath({ resource: "razaEventAttendances", type: "create" });

  const toAttendance = (majlisId: string | number) =>
    `${attendanceBase}?razaEventId=${encodeURIComponent(String(majlisId))}`;

  const isPast = attendanceTab === "past";

  const missingAttendanceRowSx = {
    bgcolor: alpha(theme.palette.warning.main, 0.14),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  };

  const datagridMissingAttendanceRowSx = {
    "&&&": {
      bgcolor: alpha(theme.palette.warning.main, 0.14),
      borderLeft: `4px solid ${theme.palette.warning.main}`,
    },
    "&&&:hover": {
      bgcolor: alpha(theme.palette.warning.main, 0.2),
    },
  };

  const isMissingAttendance = (record: RaRecord) => isPast && pastAttendanceCount(record) === 0;

  const listRowSx = (record: RaRecord) => ({
    borderBottom: 1,
    borderBottomColor: "divider",
    ...(isMissingAttendance(record) ? missingAttendanceRowSx : {}),
  });

  const attendancePathForRecord = (record: RaRecord) => toAttendance(record.id);

  return (
    <>
      <Title title="Ohbat Attendance" />
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, bgcolor: "background.paper" }}>
        <Tabs
          value={attendanceTab}
          onChange={(_, v) => setAttendanceTab(v)}
          aria-label="Ohbat majlis by date"
        >
          <Tab label="Upcoming" value="upcoming" />
          <Tab label="Past" value="past" />
        </Tabs>
      </Box>
      <List
        resource="razaEventsUpcoming"
        pagination={false}
        perPage={500}
        title={false}
        queryOptions={{ meta: { attendanceScope: attendanceTab } }}
      >
        {isNarrow ? (
          <SimpleList
            rowClick={isPast ? (_id, _resource, record) => attendancePathForRecord(record) : false}
            primaryText={(r: RaRecord) => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                  <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>
                    {razaEventTitle(r)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatMajlisDateUtc(r.date)} · {(r.ohbatType as string) || "—"} ·{" "}
                    {formatRazaEventStartTimeLabel(r.startTime as string)}
                  </Typography>
                </Box>
                {!isPast && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ flexShrink: 0, whiteSpace: "nowrap", alignSelf: "flex-start" }}
                    component={Link}
                    to={toAttendance(r.id)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Record
                  </Button>
                )}
              </Box>
            )}
            secondaryText={(r: RaRecord) =>
              [
                (() => {
                  const label = formatRazaEventSadaratListLabel(r, { empty: "" });
                  return label ? `Sadarat: ${label}` : null;
                })(),
                (r.khidmatguzar as { Full_Name?: string } | undefined)?.Full_Name ||
                r.khidmatguzarItsNo
                  ? `Khidmat: ${(r.khidmatguzar as { Full_Name?: string })?.Full_Name || String(r.khidmatguzarItsNo)}`
                  : null,
                (r.zakereen as { Full_Name?: string } | undefined)?.Full_Name || r.zakereenItsNo
                  ? `Zakereen: ${(r.zakereen as { Full_Name?: string })?.Full_Name || String(r.zakereenItsNo)}`
                  : null,
                r.mobileNo ? `Contact: ${String(r.mobileNo)}` : null,
                [r.hostSector, r.hostSubSector].filter(Boolean).join(" · ") || null,
                r.date ? `Day: ${formatMajlisDayOfWeekUtc(r.date)}` : null,
                r.date ? `Hijri: ${formatMajlisHijriUtc(r.date)}` : null,
              ]
                .filter(Boolean)
                .join(" · ") || "—"
            }
            tertiaryText={
              isPast
                ? (r: RaRecord) => (
                    <Typography
                      variant="caption"
                      color={pastAttendanceCount(r) === 0 ? "warning.dark" : "text.secondary"}
                      fontWeight={pastAttendanceCount(r) === 0 ? 600 : 400}
                    >
                      Attendance records: {pastAttendanceCount(r)}
                    </Typography>
                  )
                : undefined
            }
            rowSx={listRowSx}
          />
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <Datagrid
              bulkActionButtons={false}
              rowClick={
                isPast ? (_id, _resource, record) => attendancePathForRecord(record) : false
              }
              rowSx={(record) => ({
                ...(isMissingAttendance(record) ? datagridMissingAttendanceRowSx : {}),
              })}
              sx={{ minWidth: isPast ? 920 : 1040 }}
            >
              <TextField source="ohbatType" label="Type" />
              <FunctionField
                label="Time"
                render={(r) => formatRazaEventStartTimeLabel(r.startTime as string)}
              />
              <FunctionField label="Date (UTC)" render={(r) => formatMajlisDateUtc(r.date)} />
              <FunctionField label="Day" render={(r) => formatMajlisDayOfWeekUtc(r.date)} />
              <FunctionField label="Hijri" render={(r) => formatMajlisHijriUtc(r.date)} />
              {isPast && (
                <FunctionField label="Attendance" render={(r) => pastAttendanceCount(r)} />
              )}
              <FunctionField label="Sadarat" render={(r) => formatRazaEventSadaratListLabel(r)} />
              <TextField source="hostItsNo" label="Host ITS" />
              <TextField source="hostName" label="Host name" emptyText="—" />
              <TextField source="hostSector" label="Sector" emptyText="—" />
              <TextField source="hostSubSector" label="Sub-sector" emptyText="—" />
              {!isPast && (
                <FunctionField
                  label=""
                  render={(r) => (
                    <Button
                      variant="outlined"
                      size="small"
                      component={Link}
                      to={toAttendance(r.id)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Record attendance
                    </Button>
                  )}
                />
              )}
            </Datagrid>
          </Box>
        )}
      </List>
    </>
  );
}
